/**
 * Build Quick Conversation selected user IDs from current membership
 * role-profile participation (humans only).
 *
 * @param {Array<{id?: number|string, is_agent?: boolean, is_conversation_participant?: boolean}>|null|undefined} roleProfiles
 * @returns {number[]}
 */
export function resolveQuickConversationSelectedUsersFromParticipation(
    roleProfiles,
) {
    return (roleProfiles ?? [])
        .filter(
            (roleProfile) =>
                roleProfile?.id != null &&
                !roleProfile.is_agent &&
                Boolean(roleProfile.is_conversation_participant),
        )
        .map((roleProfile) => Number(roleProfile.id));
}

/**
 * Add or remove a user from Quick Conversation selection with numeric ID
 * normalization so Members ↔ Quick Conversation stay in sync.
 *
 * @param {Array<number|string>|null|undefined} selectedUserIds
 * @param {number|string|null|undefined} userId
 * @param {boolean} isParticipant
 * @returns {number[]}
 */
export function syncQuickConversationSelectedUserIds(
    selectedUserIds,
    userId,
    isParticipant,
) {
    if (userId == null || Number.isNaN(Number(userId))) {
        return [...(selectedUserIds ?? [])].map((id) => Number(id));
    }

    const normalizedId = Number(userId);
    const currentIds = (selectedUserIds ?? []).map((id) => Number(id));

    if (isParticipant) {
        if (currentIds.includes(normalizedId)) {
            return currentIds;
        }

        return [...currentIds, normalizedId];
    }

    return currentIds.filter((id) => id !== normalizedId);
}

/**
 * Cache key used by Quick Conversation to restore selection for a resource.
 *
 * @param {Record<string, string>|null|undefined} resourceTypes
 * @param {{type?: string, type_id?: number|string, state?: {type_id?: number|string}}|null|undefined} conversation
 * @returns {string|null}
 */
export function resolveQuickConversationCacheKey(resourceTypes, conversation) {
    if (!conversation?.type) {
        return null;
    }

    const resourceType = resourceTypes?.[conversation.type];
    const typeId = conversation.state?.type_id ?? conversation.type_id;

    if (!resourceType || typeId == null) {
        return null;
    }

    return `${resourceType}:${typeId}`;
}

/**
 * Cache key written when Membership seeds a Quick Conversation draft.
 * Never falls back to a previously stored key — that would pin one item's
 * draft onto another when the current resource key is temporarily unknown.
 *
 * @param {string|null|undefined} providedCacheKey
 * @param {string|null|undefined} resolvedCacheKey
 * @returns {string|null}
 */
export function resolveQuickConversationSeededCacheKey(
    providedCacheKey,
    resolvedCacheKey,
) {
    return providedCacheKey ?? resolvedCacheKey ?? null;
}

/**
 * Whether Quick Conversation should wipe its draft because the resource changed.
 * Any key change after the initial watch run (including null ↔ real) resets so
 * a null Membership seed cannot be carried onto another item.
 *
 * @param {string|null|undefined} cacheKey
 * @param {string|null|undefined} previousCacheKey
 * @returns {boolean}
 */
export function shouldResetQuickConversationOnCacheKeyChange(
    cacheKey,
    previousCacheKey,
) {
    // First watch run (immediate): previous is undefined.
    if (previousCacheKey === undefined) {
        return false;
    }

    return previousCacheKey !== cacheKey;
}

/**
 * Whether Quick Conversation can restore a Membership-seeded draft.
 * Only exact non-null key matches restore — never heal a null stored key onto
 * an arbitrary current resource (cross-item contamination).
 *
 * @param {{
 *   hasFetched: boolean,
 *   storedCacheKey: string|null|undefined,
 *   currentCacheKey: string|null|undefined,
 * }} options
 * @returns {{ canRestore: boolean, healedCacheKey: string|null|undefined }}
 */
export function resolveQuickConversationRestoreDecision({
    hasFetched,
    storedCacheKey,
    currentCacheKey,
}) {
    if (!hasFetched) {
        return { canRestore: false, healedCacheKey: storedCacheKey ?? null };
    }

    if (
        storedCacheKey != null &&
        currentCacheKey != null &&
        storedCacheKey === currentCacheKey
    ) {
        return { canRestore: true, healedCacheKey: storedCacheKey };
    }

    return { canRestore: false, healedCacheKey: storedCacheKey ?? null };
}

/**
 * Whether Membership preload may re-apply a Quick Conversation draft for the
 * active conversation. Requires an exact cache-key match — a previous item's
 * `has_fetched` flag alone must never restore onto a different resource.
 *
 * @param {{
 *   hasFetched: boolean,
 *   storedCacheKey: string|null|undefined,
 *   resourceTypes: Record<string, string>|null|undefined,
 *   conversation: {type?: string, type_id?: number|string, state?: {type_id?: number|string}}|null|undefined,
 * }} options
 * @returns {boolean}
 */
export function canRestoreQuickConversationMembershipDraftForConversation({
    hasFetched,
    storedCacheKey,
    resourceTypes,
    conversation,
}) {
    return resolveQuickConversationRestoreDecision({
        hasFetched,
        storedCacheKey,
        currentCacheKey: resolveQuickConversationCacheKey(
            resourceTypes,
            conversation,
        ),
    }).canRestore;
}

/**
 * Whether Membership UI may treat the Invitation-store Quick Conversation draft
 * as live for the active conversation. Requires an exact cache-key match so a
 * previous item's `has_fetched` flag alone cannot paint users onto another
 * work-panel entity's Membership panel.
 *
 * @param {{
 *   has_fetched_members_with_role_from_quick_conversation?: boolean,
 *   quick_conversation_cache_key?: string|null,
 *   RESOURCE_TYPES?: Record<string, string>|null,
 * }|null|undefined} invitationStore
 * @param {{type?: string, type_id?: number|string, state?: {type_id?: number|string}}|null|undefined} conversation
 * @returns {boolean}
 */
export function isQuickConversationMembershipBridgeActive(
    invitationStore,
    conversation,
) {
    return canRestoreQuickConversationMembershipDraftForConversation({
        hasFetched:
            invitationStore?.has_fetched_members_with_role_from_quick_conversation,
        storedCacheKey: invitationStore?.quick_conversation_cache_key,
        resourceTypes: invitationStore?.RESOURCE_TYPES,
        conversation,
    });
}
